import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";

export interface Column<T> {
  key: string;
  label: string;
  /** Comparable value for sorting (string or number). */
  get: (row: T) => string | number;
  /** Display renderer (defaults to String(get(row))). */
  render?: (row: T) => ReactNode;
  align?: "left" | "right";
  /** Filter widget: "multi" = multi-select combo of all values (default), "date" = from–to range, "none". */
  filter?: "multi" | "date" | "none";
  /** Value used for the multi-select combo options + matching (defaults to String(get(row))). */
  filterValue?: (row: T) => string;
  /** Millisecond timestamp for the date-range filter (defaults to Number(get(row))). */
  dateValue?: (row: T) => number;
}

type SortState = { key: string; dir: "asc" | "desc" } | null;
type DateRange = { from?: string; to?: string };

function fval<T>(c: Column<T>, row: T): string {
  const v = c.filterValue ? c.filterValue(row) : String(c.get(row) ?? "");
  return v.trim() === "" ? "—" : v;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selectedKey,
  initialSort = null,
  empty = "Brak wyników.",
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  initialSort?: SortState;
  empty?: ReactNode;
}) {
  const [sort, setSort] = useState<SortState>(initialSort);
  const [multi, setMulti] = useState<Record<string, string[]>>({});
  const [dates, setDates] = useState<Record<string, DateRange>>({});

  // Distinct options per multi-select column (over the full row set).
  const options = useMemo(() => {
    const o: Record<string, string[]> = {};
    for (const c of columns) {
      if ((c.filter ?? "multi") === "multi") {
        const set = new Set<string>();
        for (const row of rows) set.add(fval(c, row));
        o[c.key] = [...set].sort((a, b) => a.localeCompare(b, "pl", { numeric: true }));
      }
    }
    return o;
  }, [columns, rows]);

  const view = useMemo(() => {
    let r = rows;
    for (const c of columns) {
      const kind = c.filter ?? "multi";
      if (kind === "multi") {
        const sel = multi[c.key];
        if (sel && sel.length) r = r.filter((row) => sel.includes(fval(c, row)));
      } else if (kind === "date") {
        const d = dates[c.key];
        if (d && (d.from || d.to)) {
          const fromMs = d.from ? new Date(d.from + "T00:00:00").getTime() : -Infinity;
          const toMs = d.to ? new Date(d.to + "T23:59:59.999").getTime() : Infinity;
          r = r.filter((row) => {
            const ts = c.dateValue ? c.dateValue(row) : Number(c.get(row));
            if (!ts) return false; // no date on this row → excluded once a range is set
            return ts >= fromMs && ts <= toMs;
          });
        }
      }
    }
    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      if (col) {
        const dir = sort.dir === "asc" ? 1 : -1;
        r = [...r].sort((a, b) => {
          const va = col.get(a);
          const vb = col.get(b);
          const cmp =
            typeof va === "number" && typeof vb === "number"
              ? va - vb
              : String(va ?? "").localeCompare(String(vb ?? ""), "pl", { numeric: true });
          return cmp * dir;
        });
      }
    }
    return r;
  }, [rows, columns, multi, dates, sort]);

  const toggleSort = (key: string) =>
    setSort((s) => (s?.key === key ? (s.dir === "asc" ? { key, dir: "desc" } : null) : { key, dir: "asc" }));

  return (
    <div className="card" style={{ overflowX: "auto" }}>
      <table className="tbl">
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sort?.key === c.key;
              const kind = c.filter ?? "multi";
              return (
                <th key={c.key} style={{ textAlign: c.align ?? "left", verticalAlign: "top" }}>
                  <div
                    onClick={() => toggleSort(c.key)}
                    style={{
                      display: "flex", alignItems: "center", gap: 4, cursor: "pointer",
                      justifyContent: c.align === "right" ? "flex-end" : "flex-start", userSelect: "none",
                    }}
                  >
                    <span>{c.label}</span>
                    <span style={{ opacity: active ? 1 : 0.3, fontSize: 10 }}>
                      {active ? (sort!.dir === "asc" ? "▲" : "▼") : "↕"}
                    </span>
                  </div>
                  {kind === "multi" && (
                    <MultiFilter
                      options={options[c.key] ?? []}
                      selected={multi[c.key] ?? []}
                      onChange={(vals) => setMulti((m) => ({ ...m, [c.key]: vals }))}
                    />
                  )}
                  {kind === "date" && (
                    <DateFilter
                      value={dates[c.key] ?? {}}
                      onChange={(r) => setDates((d) => ({ ...d, [c.key]: r }))}
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {view.length === 0 ? (
            <tr><td colSpan={columns.length} className="empty">{empty}</td></tr>
          ) : (
            view.map((row) => {
              const k = rowKey(row);
              return (
                <tr
                  key={k}
                  className={selectedKey === k ? "selected" : ""}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={{ cursor: onRowClick ? "pointer" : "default" }}
                >
                  {columns.map((c) => (
                    <td key={c.key} style={{ textAlign: c.align ?? "left" }}>
                      {c.render ? c.render(row) : String(c.get(row) ?? "")}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---- Multi-select combo filter ----------------------------------------------

function MultiFilter({
  options, selected, onChange,
}: {
  options: string[];
  selected: string[];
  onChange: (vals: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const shown = q ? options.filter((o) => o.toLowerCase().includes(q.toLowerCase())) : options;
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  const pop: CSSProperties = {
    position: "fixed",
    top: rect ? rect.bottom + 4 : 0,
    left: rect ? Math.min(rect.left, window.innerWidth - 268) : 0,
    width: 252, zIndex: 9999,
    background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8,
    boxShadow: "0 10px 30px rgba(0,0,0,0.25)", padding: 8, fontWeight: 400,
  };

  return (
    <>
      <button
        ref={btnRef}
        className="btn sm"
        style={{ marginTop: 6, width: "100%", fontSize: 12, height: 26, padding: "0 8px", fontWeight: 400, justifyContent: "space-between" }}
        onClick={() => { setRect(btnRef.current!.getBoundingClientRect()); setOpen((o) => !o); }}
      >
        {selected.length ? `Wybrano: ${selected.length}` : "Filtruj…"} ▾
      </button>
      {open && createPortal(
        <div ref={popRef} style={pop}>
          <input
            className="input" autoFocus placeholder="szukaj…" value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ height: 28, fontSize: 12, marginBottom: 6, width: "100%" }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <button className="btn sm ghost" style={{ fontSize: 11, height: 22 }} onClick={() => onChange([...options])}>Zaznacz wszystkie</button>
            <button className="btn sm ghost" style={{ fontSize: 11, height: 22 }} onClick={() => onChange([])}>Wyczyść</button>
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {shown.length === 0 ? (
              <div className="faint" style={{ fontSize: 12, padding: "4px 2px" }}>Brak pozycji</div>
            ) : shown.map((o) => (
              <label key={o} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 2px", fontSize: 12.5, cursor: "pointer" }}>
                <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o}>{o}</span>
              </label>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

// ---- Date-range filter (from–to, native calendar) ---------------------------

function DateFilter({ value, onChange }: { value: DateRange; onChange: (r: DateRange) => void }) {
  const box: CSSProperties = { height: 26, fontSize: 12, padding: "2px 6px", width: "100%", fontWeight: 400 };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
      <input type="date" className="input" style={box} title="Od" value={value.from ?? ""}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onChange({ ...value, from: e.target.value || undefined })} />
      <input type="date" className="input" style={box} title="Do" value={value.to ?? ""}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onChange({ ...value, to: e.target.value || undefined })} />
    </div>
  );
}
